import React from 'react';
import { useTranslation } from 'react-i18next';

const FLOATING_ICONS = [
  { icon: 'bi-flower1',            top: '8%',  left: '3%',  size: 2.2, rotate: -15 },
  { icon: 'bi-music-note-beamed',  top: '18%', left: '88%', size: 1.8, rotate: 12  },
  { icon: 'bi-globe-asia-australia', top: '55%', left: '92%', size: 2.4, rotate: 0 },
  { icon: 'bi-people-fill',        top: '70%', left: '4%',  size: 1.9, rotate: 8   },
  { icon: 'bi-star-fill',          top: '38%', left: '91%', size: 1.4, rotate: -8  },
  { icon: 'bi-camera-fill',        top: '82%', left: '87%', size: 1.6, rotate: 15  },
  { icon: 'bi-heart-fill',         top: '12%', left: '78%', size: 1.3, rotate: -10 },
  { icon: 'bi-flag-fill',          top: '65%', left: '95%', size: 1.2, rotate: 5   },
];

export default function Hero() {
  const { t } = useTranslation();

  return (
    <section
      className="bg-festival text-white position-relative overflow-hidden"
      style={{ minHeight: '92vh', display: 'flex', alignItems: 'center' }}
    >
      {/* Soft glow blobs */}
      <div style={{
        position: 'absolute', top: '-120px', right: '-80px',
        width: '500px', height: '500px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,214,0,.18) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-80px', left: '-80px',
        width: '400px', height: '400px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,.1) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Floating icons */}
      {FLOATING_ICONS.map((item, i) => (
        <i
          key={i}
          className={`bi ${item.icon} position-absolute`}
          style={{
            top: item.top, left: item.left,
            fontSize: `${item.size}rem`,
            opacity: 0.12,
            transform: `rotate(${item.rotate}deg)`,
            pointerEvents: 'none',
            color: '#FFD600',
          }}
        />
      ))}

      <div className="container position-relative" style={{ zIndex: 1 }}>
        <div className="row align-items-center g-5">

          {/* Left: copy */}
          <div className="col-lg-7 fade-in-up">
            {/* Badge */}
            <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-4"
              style={{
                background: 'rgba(255,214,0,.15)',
                border: '1.5px solid rgba(255,214,0,.4)',
              }}>
              <i className="bi bi-stars" style={{ color: '#FFD600' }} />
              <span style={{ fontSize: '.85rem', fontWeight: 700, letterSpacing: '.06em', color: '#FFD600' }}>
                {t('hero.badge')}
              </span>
            </div>

            {/* Title */}
            <h1 className="display-3 lh-1 mb-3" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 900 }}>
              <span style={{ color: '#FFD600' }}>{t('hero.title')}</span>
              <br />
              <span className="text-white">{t('hero.titleHighlight')}</span>
            </h1>

            <p className="fs-5 mb-4" style={{ color: 'rgba(255,255,255,.82)', whiteSpace: 'pre-line', lineHeight: 1.8 }}>
              {t('hero.subtitle')}
            </p>

            <div className="d-flex flex-wrap gap-3 mt-4">
              <a href="#donate" className="btn btn-donate fs-5 px-5 py-3">
                <i className="bi bi-heart-fill me-2" />
                {t('hero.donateBtn')}
              </a>
              <a href="#about" className="btn btn-outline-light rounded-pill px-5 py-3 fs-5">
                {t('hero.learnMore')}
                <i className="bi bi-arrow-down ms-2" />
              </a>
            </div>
          </div>

          {/* Right: stat cards */}
          <div className="col-lg-5">
            <div className="row g-3">
              {[
                { icon: 'bi-mortarboard-fill', key: 'stat1', val: 'stat1Val', color: '#FFD600' },
                { icon: 'bi-people-fill',      key: 'stat2', val: 'stat2Val', color: '#FFD600' },
                { icon: 'bi-globe2',           key: 'stat3', val: 'stat3Val', color: '#FFD600' },
              ].map((s, i) => (
                <div key={i} className="col-4">
                  <div
                    className="text-center p-3 rounded-3 card-lift"
                    style={{
                      background: 'rgba(255,255,255,.1)',
                      backdropFilter: 'blur(12px)',
                      border: '1.5px solid rgba(255,214,0,.3)',
                    }}
                  >
                    <div className="mx-auto mb-2" style={{
                      width: 44, height: 44,
                      background: 'rgba(255,214,0,.2)',
                      border: '1.5px solid rgba(255,214,0,.5)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: '12px',
                    }}>
                      <i className={`bi ${s.icon}`} style={{ color: '#FFD600', fontSize: '1.2rem' }} />
                    </div>
                    <div style={{ fontSize: '1.7rem', fontWeight: 800, color: '#FFD600', lineHeight: 1.2 }}>
                      {t(`hero.${s.val}`)}
                    </div>
                    <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.65)', marginTop: '3px' }}>
                      {t(`hero.${s.key}`)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Feature pills */}
            <div
              className="mt-3 p-4 rounded-4"
              style={{
                background: 'rgba(255,255,255,.1)',
                backdropFilter: 'blur(12px)',
                border: '1.5px solid rgba(255,214,0,.25)',
              }}
            >
              <div className="row g-3">
                {[
                  { icon: 'bi-palette-fill',    labelKey: 'pillar1' },
                  { icon: 'bi-cup-hot-fill',    labelKey: 'pillar2' },
                  { icon: 'bi-music-note-list', labelKey: 'pillar3' },
                  { icon: 'bi-camera2',         labelKey: 'pillar4' },
                ].map((p, i) => (
                  <div key={i} className="col-6">
                    <div className="d-flex align-items-center gap-2">
                      <div style={{
                        width: 34, height: 34, borderRadius: '10px', flexShrink: 0,
                        background: 'rgba(255,214,0,.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <i className={`bi ${p.icon}`} style={{ color: '#FFD600' }} />
                      </div>
                      <span style={{ fontSize: '.8rem', color: 'rgba(255,255,255,.8)', fontWeight: 500 }}>
                        {t(`hero.${p.labelKey}`)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Scalloped wave bottom */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        lineHeight: 0, pointerEvents: 'none',
      }}>
        <svg viewBox="0 0 1200 60" preserveAspectRatio="none" style={{ width: '100%', height: '60px', display: 'block' }}>
          <path d="M0,30 C100,60 200,0 300,30 C400,60 500,0 600,30 C700,60 800,0 900,30 C1000,60 1100,0 1200,30 L1200,60 L0,60 Z" fill="#ffffff" />
        </svg>
      </div>
    </section>
  );
}
